import React, { useEffect, useMemo, useRef, useState } from "react";
import { TextField, Box, Typography, CircularProgress } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { getEmpsisImobilizados } from "../../services/kanban";
import { i18n } from "../../translate/i18n";

// Marcador gravado em KanbanBoardFieldDefinition.validation que liga o autocomplete
// de imobilizados num campo de texto (ver backend/.../templates/locacoesNovas.ts).
export const IMOBILIZADO_DATA_SOURCE = "empsis_imobilizado";

// validation e uma coluna JSON: dependendo do caminho pode chegar ja como objeto
// ou ainda como string (mesma tolerancia que o CardModal aplica a fieldOptions).
const lerValidation = (field) => {
  const raw = field?.validation;
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }
  return raw;
};

export const isImobilizadoField = (field) =>
  lerValidation(field)?.dataSource === IMOBILIZADO_DATA_SOURCE;

// Espelha MIN_CARACTERES_BUSCA do backend: com menos que isso quase todo o
// catálogo casaria, e a lista deixaria de ajudar.
export const MIN_CARACTERES_BUSCA = 3;

const DEBOUNCE_MS = 350;

/**
 * Campo de patrimônio: seleção única cujas opções vêm da busca de imobilizados
 * da empsis. A consulta só dispara a partir de MIN_CARACTERES_BUSCA letras e é
 * debounced; o filtro roda no servidor, então as opções são exatamente o que a
 * API devolveu.
 *
 * Grava apenas o código do patrimônio — é o que a empsis espera de volta em
 * INTEGRACAO_LEAD_CAMPO_PATRIMONIO_N.
 */
const ImobilizadoAutocomplete = ({
  label,
  value,
  onChange,
  error = false,
  helperText,
  placeholder,
  // Item já resolvido para o código gravado no card, para o campo abrir
  // mostrando a descrição em vez do código cru.
  opcaoResolvida = null,
}) => {
  const [termo, setTermo] = useState("");
  const [opcoes, setOpcoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const buscaAtual = useRef(0);

  const valorAtual = value === null || value === undefined ? "" : String(value);
  const buscaAtiva = termo.length >= MIN_CARACTERES_BUSCA;

  // O MUI precisa de um objeto como value quando não é freeSolo. Preferimos o
  // item vindo da busca; senão o resolvido; senão um stub só com o código.
  const opcaoAtual = useMemo(() => {
    if (!valorAtual) return null;
    return (
      opcoes.find((opcao) => opcao.patrimonio === valorAtual) ||
      (opcaoResolvida?.patrimonio === valorAtual ? opcaoResolvida : null) || {
        patrimonio: valorAtual,
        descricao: "",
      }
    );
  }, [valorAtual, opcoes, opcaoResolvida]);

  useEffect(() => {
    if (!buscaAtiva) {
      setOpcoes([]);
      setCarregando(false);
      return undefined;
    }

    // Ignora respostas de buscas antigas que chegarem fora de ordem.
    const idBusca = buscaAtual.current + 1;
    buscaAtual.current = idBusca;

    setCarregando(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await getEmpsisImobilizados({ searchParam: termo });
        if (buscaAtual.current !== idBusca) return;
        setOpcoes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erro ao buscar imobilizados da empsis:", err);
        if (buscaAtual.current === idBusca) setOpcoes([]);
      } finally {
        if (buscaAtual.current === idBusca) setCarregando(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [termo, buscaAtiva]);

  // O MUI avisa no console quando o value nao esta entre as options. Manter o
  // item selecionado sempre na lista evita isso e deixa a escolha atual visivel.
  const opcoesVisiveis = useMemo(() => {
    if (!opcaoAtual) return opcoes;
    return opcoes.some((opcao) => opcao.patrimonio === opcaoAtual.patrimonio)
      ? opcoes
      : [opcaoAtual, ...opcoes];
  }, [opcoes, opcaoAtual]);

  const textoAuxiliar = (() => {
    if (helperText) return helperText;
    if (opcaoAtual?.descricao) {
      return `${opcaoAtual.descricao} · ${i18n.t("imobilizadoAutocomplete.availability", {
        available: opcaoAtual.qtdeDisponivel,
        total: opcaoAtual.qtdeTotal,
      })}`;
    }
    return undefined;
  })();

  return (
    <Autocomplete
      autoHighlight
      handleHomeEndKeys
      options={opcoesVisiveis}
      loading={carregando}
      // O servidor já devolve só o que casou com a busca.
      filterOptions={(options) => options}
      value={opcaoAtual}
      onChange={(_, opcao) => onChange(opcao ? opcao.patrimonio : "")}
      // Só o que o usuário digita vira termo de busca; o texto que o MUI repõe
      // ao selecionar/limpar (motivo "reset"/"clear") não dispara nova consulta.
      onInputChange={(_, novoInput, motivo) =>
        setTermo(motivo === "input" ? novoInput.trim() : "")
      }
      getOptionSelected={(opcao, selecionada) =>
        opcao?.patrimonio === selecionada?.patrimonio
      }
      getOptionLabel={(opcao) =>
        opcao?.descricao ? `${opcao.patrimonio} — ${opcao.descricao}` : opcao?.patrimonio || ""
      }
      renderOption={(opcao) => (
        <Box>
          <Typography variant="body2">
            <strong>{opcao.patrimonio}</strong> — {opcao.descricao}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block">
            {i18n.t("imobilizadoAutocomplete.availability", {
              available: opcao.qtdeDisponivel,
              total: opcao.qtdeTotal,
            })}
          </Typography>
        </Box>
      )}
      noOptionsText={
        buscaAtiva
          ? i18n.t("imobilizadoAutocomplete.noOptions")
          : i18n.t("imobilizadoAutocomplete.minChars", { min: MIN_CARACTERES_BUSCA })
      }
      loadingText={i18n.t("imobilizadoAutocomplete.loading")}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          margin="dense"
          fullWidth
          error={error}
          helperText={textoAuxiliar}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {carregando ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ImobilizadoAutocomplete;
